/**
 * One-click Local: scan this computer, pick models, real Ollama download.
 * Soft≠Live: installed ≠ answering until published for the AI Team.
 */
import { useEffect, useState } from "react";
import { html, Card, Alert } from "./ui.js";
import { api } from "./api.js";

function jobTone(status) {
  if (status === "ready") return "go";
  if (status === "failed") return "bad";
  return "warn";
}

export function LocalComputerPanel({
  toast, seat, tenantWrites, onPublished, tags, onTags, picked, onToggle, busy,
}) {
  const [jobs, setJobs] = useState([]);
  const [pulling, setPulling] = useState(false);
  const catalog = Array.isArray(tags && tags.catalog) ? tags.catalog : [];
  const installed = catalog.filter((m) => m && m.installed);
  const downloadable = catalog.filter((m) => m && !m.installed);
  const selected = downloadable.filter((m) => picked && picked[m.name]);
  const runtimes = Array.isArray(tags && tags.runtimes) ? tags.runtimes : [];
  const chosen = String((tags && (tags.chosen_base || tags.base_url)) || "").trim();
  const pullOk = !!(tags && tags.pull_capable);

  useEffect(() => {
    const active = jobs.filter((j) => j && (j.status === "queued" || j.status === "pulling"));
    if (!active.length) return undefined;
    const t = setInterval(async () => {
      const next = [];
      for (const job of jobs) {
        if (!job || !job.job_id) continue;
        if (job.status !== "queued" && job.status !== "pulling") {
          next.push(job);
          continue;
        }
        try {
          const r = await api("/ai/models/local-pull/jobs/" + encodeURIComponent(job.job_id));
          next.push((r && r.job) || job);
        } catch (_e) {
          next.push(job);
        }
      }
      setJobs(next);
      if (next.some((j) => j && j.status === "ready")) {
        onTags && onTags({ silent: true });
      }
    }, 1600);
    return () => clearInterval(t);
  }, [jobs.map((j) => String((j && j.job_id) || "") + ":" + String((j && j.status) || "")).join("|")]);

  const downloadSelected = async () => {
    if (!selected.length) {
      toast && toast("Select one or more models to download.", "warn");
      return;
    }
    if (!pullOk) {
      toast && toast("Ollama is not reachable on this computer yet. Install Ollama, then click Local again.", "warn");
      return;
    }
    setPulling(true);
    const created = [];
    try {
      for (const row of selected) {
        const r = await api("/ai/models/local-pull/jobs", {
          method: "POST",
          body: {
            live: true,
            name: row.name,
            admin_digest: row.name,
            base_url: chosen,
          },
        });
        if (r && r.job) created.push(r.job);
      }
      setJobs((prev) => [...created, ...prev].slice(0, 12));
      toast && toast(
        created.length
          ? `Downloading ${created.length} model(s) onto this computer`
          : "Could not start download",
        created.length ? "warn" : "bad",
      );
    } catch (e) {
      toast && toast(String((e && e.message) || e || "Download did not start"), "bad");
    } finally {
      setPulling(false);
    }
  };

  const publishInstalled = async () => {
    if (!seat || !seat.tenantOpsAllowed) {
      toast && toast("Account publish needs an admin session.", "warn");
      return;
    }
    const names = installed.map((m) => m.name).filter((n) => picked && picked[n]);
    const use = names.length ? names : installed.map((m) => m.name).slice(0, 1);
    if (!use.length) {
      toast && toast("Scan Local first, then pick an installed model.", "warn");
      return;
    }
    if (!chosen) {
      toast && toast("No local Ollama URL was found.", "warn");
      return;
    }
    let ok = 0;
    for (const name of use) {
      const r = await tenantWrites.save({
        label: name,
        provider: "ollama",
        base_url: chosen,
        text_model: name,
        cost_class: "local",
        is_default: ok === 0,
      });
      if (r) ok += 1;
    }
    if (ok) {
      onPublished && onPublished(ok, chosen);
      toast && toast(`${ok} local brain(s) ready for the AI Team`, "go");
    } else toast && toast(String(tenantWrites.error || "Could not publish."), "bad");
  };

  return html`<div data-cmc-local-computer="1">
    <${Card} title="On this computer"
      meta=${tags.loading ? "scanning" : (tags.ok ? `${installed.length} installed` : "Search")}>
    ${tags.ok && !pullOk ? html`<${Alert} tone="warn" title="Runtime reached, pull not available"
      say="A local OpenAI-compat surface answered, but it cannot install weights. Point at Ollama :11434 to download."/>` : null}
    ${runtimes.length ? html`<p className="note" data-cmc-discover-honesty="1">
      ${runtimes.filter((r) => r.ok).length} runtime(s) · ${chosen || "no base"} · Tags listed · not live inference
    </p>` : null}
    ${installed.length ? html`<div className="models-desk__tags">
      <span className="eyebrow">Installed</span>
      <div className="models-desk__chips">
        ${installed.map((m) => html`<label key=${"in-"+m.name} className="models-desk__chip"
          data-on=${picked && picked[m.name] ? "1" : "0"}>
          <input type="checkbox" checked=${!!(picked && picked[m.name])}
            onChange=${() => onToggle(m.name)}/>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${() => onToggle(m.name)}>
            ${m.name}${m.parameter_size ? ` · ${m.parameter_size}` : ""}
          </button>
        </label>`)}
      </div>
    </div>` : null}
    ${downloadable.length ? html`<div className="models-desk__tags">
      <span className="eyebrow">Download to this computer</span>
      <div className="models-desk__chips">
        ${downloadable.map((m) => html`<label key=${"dl-"+m.name} className="models-desk__chip"
          data-on=${picked && picked[m.name] ? "1" : "0"}>
          <input type="checkbox" checked=${!!(picked && picked[m.name])}
            onChange=${() => onToggle(m.name)}/>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${() => onToggle(m.name)}>
            ${m.name}${m.approx_gb ? ` · ~${m.approx_gb} GB` : ""}${m.purpose ? ` · ${m.purpose}` : ""}
          </button>
        </label>`)}
      </div>
    </div>` : null}
    ${tags.ok && !catalog.length ? html`<p className="note">No local model matches</p>` : null}
    ${!tags.loading && !tags.ok && tags.reason ? html`<p className="note">Nothing found on this computer.</p>` : null}
    ${jobs.map((job) => html`<div key=${job.job_id} className="models-desk__pull" data-tone=${jobTone(job.status)}>
      <span>${job.name || job.admin_digest}</span>
      <b>${job.status}</b>
      <em>${job.progress_pct || 0}%</em>
      <i className="models-desk__bar"><i style=${{ width: (job.progress_pct || 0) + "%" }}/></i>
      <small>${job.note || job.reason || ""}</small>
    </div>`)}
    <div className="btn-row" style=${{ marginTop: 10 }}>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${tags.loading || busy}
        onClick=${() => onTags && onTags({})}>
        ${tags.loading ? "Searching…" : "Search this computer"}
      </button>
      <button type="button" className="btn btn--go btn--sm"
        disabled=${pulling || busy || !selected.length}
        onClick=${downloadSelected}>
        ${pulling ? "Starting…" : `Download selected (${selected.length})`}
      </button>
      ${seat && seat.tenantOpsAllowed ? html`<button type="button" className="btn btn--sm"
        disabled=${busy || !installed.length}
        onClick=${publishInstalled}>
        ${busy ? "Publishing…" : "Use for AI Team"}
      </button>` : null}
    </div>
  <//>
  </div>`;
}

export default LocalComputerPanel;
